import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const CAREGIVER_ROLES = [
  { value: 'mum',     label: 'Mum',     emoji: '👩' },
  { value: 'dad',     label: 'Dad',     emoji: '👨' },
  { value: 'nan',     label: 'Nan',     emoji: '👵' },
  { value: 'grandad', label: 'Grandad', emoji: '👴' },
  { value: 'nanny',   label: 'Nanny',   emoji: '🧑‍🍼' },
  { value: 'partner', label: 'Partner', emoji: '🤝' },
  { value: 'carer',   label: 'Carer',   emoji: '💙' },
  { value: 'other',   label: 'Other',   emoji: '👤' },
];

/**
 * UserProfileSetup — modal that prompts a new user to set their display name
 * and caregiver role. Shown by AppLayout when profile.displayName is not set.
 *
 * On save: writes to Firestore users/{uid} via saveProfile().
 * On dismiss: writes { dismissed: true } so the modal doesn't show again.
 */
export default function UserProfileSetup({ onClose }) {
  const { user, saveProfile } = useAuth();
  const [name, setName]       = useState('');
  const [role, setRole]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Please enter your name.'); return; }
    setSaving(true);
    try {
      await saveProfile({
        displayName: name.trim(),
        role,
        email: user?.email || '',
        setupAt: new Date().toISOString(),
      });
      onClose();
    } catch (err) {
      setError('Could not save — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = async () => {
    await saveProfile({ dismissed: true }).catch(() => {});
    onClose();
  };

  return (
    <div
      className="child-switcher-backdrop"
      style={{ zIndex: 9300 }} // above tutorial tooltip (9200)
      onClick={(e) => { if (e.target === e.currentTarget) handleDismiss(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Set up your profile"
    >
      <div className="child-switcher-modal" style={{ maxWidth: 420 }}>
        <div className="child-switcher-modal-header">
          <span className="child-switcher-modal-title">👋 Set up your profile</span>
          <button
            className="child-switcher-modal-close"
            onClick={handleDismiss}
            aria-label="Skip for now"
          >
            ✕
          </button>
        </div>
        <div className="child-switcher-modal-body">
          <p style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '1.25rem' }}>
            This helps caregivers see who logged each entry in the activity feed.
          </p>

          <form onSubmit={handleSave}>
            {/* Name input */}
            <div className="mb-3">
              <label className="form-label" htmlFor="profile-name" style={{ fontWeight: 600 }}>
                Your name
              </label>
              <input
                id="profile-name"
                type="text"
                className="form-control"
                placeholder="e.g. Sarah"
                value={name}
                onChange={(e) => { setName(e.target.value); setError(''); }}
                autoFocus
                maxLength={40}
              />
            </div>

            {/* Role picker */}
            <div className="mb-4">
              <label className="form-label" style={{ fontWeight: 600 }}>
                Your role <span style={{ color: 'var(--text-light)', fontWeight: 400 }}>(optional)</span>
              </label>
              <div className="profile-role-grid">
                {CAREGIVER_ROLES.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    className={`profile-role-btn${role === r.value ? ' active' : ''}`}
                    onClick={() => setRole(role === r.value ? '' : r.value)}
                  >
                    <span className="profile-role-emoji" aria-hidden="true">{r.emoji}</span>
                    <span className="profile-role-label">{r.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem' }}>
                {error}
              </div>
            )}

            <div className="d-flex gap-2 justify-content-between align-items-center">
              <button
                type="button"
                className="btn btn-link text-secondary p-0"
                style={{ fontSize: '0.825rem' }}
                onClick={handleDismiss}
              >
                Skip for now
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
